import { palettes, type Palette, type ThemeMode } from './tokens';

/**
 * Projects the active palette onto CSS custom properties (`--ol-*`) on <html>, so the structural
 * styles in global.css track the AntD theme without prop-drilling. tokens.ts stays canonical.
 *
 * P1 white-labeling: an optional per-hub `override` (a partial Palette, server-validated against
 * the closed 20-field contract) merges over the mode's defaults. No override ⇒ byte-identical
 * output to the pre-theming build.
 */
export function applyThemeVars(mode: ThemeMode, override?: Partial<Palette>): void {
  const p: Palette = { ...palettes[mode], ...override };
  const root = document.documentElement;
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
  const set = (k: string, v: string) => root.style.setProperty(k, v);

  set('--ol-bg-base', p.bgBase);
  set('--ol-bg-container', p.bgContainer);
  set('--ol-bg-elevated', p.bgElevated);
  set('--ol-bg-subtle', p.bgSubtle);
  set('--ol-border', p.border);
  set('--ol-border-strong', p.borderStrong);
  set('--ol-border-subtle', p.borderSubtle);
  set('--ol-text-primary', p.textPrimary);
  set('--ol-text-secondary', p.textSecondary);
  set('--ol-text-tertiary', p.textTertiary);
  set('--ol-text-inverse', p.textInverse);
  set('--ol-primary', p.primary);
  set('--ol-primary-hover', p.primaryHover);
  // Completes the Palette→vars projection (was the one unprojected field); nothing in
  // global.css consumes it yet, so un-themed rendering is unchanged — but hub CSS may var() it.
  set('--ol-primary-active', p.primaryActive);
  set('--ol-focus-ring', p.focusRing);
  set('--ol-positive', p.positive);
  set('--ol-attention', p.attention);
  set('--ol-negative', p.negative);
  set('--ol-info', p.info);
  set('--ol-processing', p.processing);
}

/**
 * Extracts the Palette override for `mode` from a hub theme's `tokens` value, which is either a
 * flat override or per-mode ({ light: {...}, dark: {...} }). Unknown fields were already refused
 * server-side; this stays defensive anyway (only known Palette keys pass through).
 */
export function resolveOverride(
  tokens: Record<string, unknown> | null | undefined,
  mode: ThemeMode,
): Partial<Palette> {
  if (!tokens) return {};
  const source =
    'light' in tokens || 'dark' in tokens
      ? ((tokens[mode] ?? {}) as Record<string, unknown>)
      : tokens;
  const out: Partial<Palette> = {};
  for (const key of Object.keys(palettes.light) as (keyof Palette)[]) {
    const value = source[key];
    if (typeof value === 'string' && value.length > 0) out[key] = value;
  }
  return out;
}

/**
 * Resolve the applicant's preferred theme from the OS. We default to LIGHT (friendlier for the
 * general public in daylight) and only go dark when the OS explicitly asks. No manual toggle — the
 * applicant surface stays minimal; theme follows the device.
 */
export function resolveTheme(): ThemeMode {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}
